import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import api from "../lib/api.ts";
import usePageMeta from "../hooks/usePageMeta.js";
import {
  Building2,
  Phone,
  Mail,
  MapPin,
  Linkedin,
  Twitter,
  Instagram,
  Loader2,
  HelpCircle,
} from "lucide-react";
import { Link } from "react-router-dom";

const ContactPage = () => {
  usePageMeta("contact_us");

  const { user } = useAuth();
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ text: "", type: "" });
  const [dynamicContent, setDynamicContent] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDynamicContent = async () => {
    try {
      setLoading(true);
      const lang = language === "ar" ? "ar" : "en";
      const response = await api.get(`/pagesettings/contactus?lang=${lang}`);
      setDynamicContent(response.data);
    } catch (error) {
      console.error("Error fetching contact page content:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDynamicContent();
  }, [language]);

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        name: user.name || "",
        email: user.email || "",
        phone: user.mobileNo || "",
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage({ text: "", type: "" });

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
      };

      const response = await api.post("/email/contact-us", payload);

      if (response.status === 200 || response.status === 201) {
        setSubmitMessage({ text: translate("contactPage.messages.success"), type: "success" });
        setFormData({
          name: user?.name || "",
          email: user?.email || "",
          phone: user?.mobileNo || "",
          subject: "",
          message: "",
        });
      }
    } catch (error) {
      setSubmitMessage({ text: translate("contactPage.messages.error"), type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ──────────────────────────────────────────────
  // Extract data safely
  // ──────────────────────────────────────────────
  const faqTeaser = dynamicContent?.faq_teaser?.[0] || {};
  const contactDetails = dynamicContent?.contact_details?.[0] || {};
  const contactCardsRaw = dynamicContent?.contact_info_cards || [];
  const companyInfo = dynamicContent?.contact_company_info?.[0] || {};
  const socialLinksRaw = dynamicContent?.contact_social_links || [];
  const rightImage = dynamicContent?.contact_us_form_right_image?.[0]?.imageUrl?.value || "";
  const afterFormSection = dynamicContent?.after_contact_form_section?.[0] || {};

  const contactCards = Array.isArray(contactCardsRaw) ? contactCardsRaw : [];
  const socialLinks = Array.isArray(socialLinksRaw) ? socialLinksRaw : [];

  // Icon mapping for contact cards
  const iconMap = {
    location: MapPin,
    phone: Phone,
    email: Mail,
    custom: HelpCircle,
  };

  const socialIconMap = {
    linkedin: Linkedin,
    twitter: Twitter,
    instagram: Instagram,
    facebook: HelpCircle,
    youtube: HelpCircle,
    custom: HelpCircle,
  };

  const getCardIcon = (card) => {
    let iconType = card.icon_type?.value?.toLowerCase();
    if (!iconType) {
      const titleLower = (card.card_title?.value || "").toLowerCase();
      const contentLower = (card.main_content?.value || "").toLowerCase();
      if (titleLower.includes("email") || contentLower.includes("@")) return Mail;
      if (titleLower.includes("phone") || contentLower.match(/\d{3,}/)) return Phone;
      if (titleLower.includes("office") || titleLower.includes("address") || titleLower.includes("location")) return MapPin;
      return HelpCircle;
    }
    return iconMap[iconType] || HelpCircle;
  };

  // ─── Filter only real external social links ───
  const validSocialLinks = socialLinks
    .filter((s) => {
      const url = s.link_url?.value?.trim();
      if (!url) return false;
      // Exclude localhost, 127.0.0.1, empty, #, javascript:, etc.
      if (
        url === "#" ||
        url.startsWith("http://localhost") ||
        url.startsWith("https://localhost") ||
        url.startsWith("http://127.0.0.1") ||
        url.startsWith("file://") ||
        url.startsWith("javascript:")
      ) {
        return false;
      }
      return true;
    })
    .map((s) => ({
      url: s.link_url.value.trim(),
      platform: (s.platform?.value || "custom").toLowerCase(),
      customIcon: s.custom_icon?.value || null,
    }));

  return (
    <div className={`min-h-screen bg-page ${isRTL ? "text-right" : "text-left"}`} dir={isRTL ? "rtl" : "ltr"}>
      <div className="  ">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="animate-spin text-blue-500" size={48} />
            <p className="mt-4 text-gray-400">{translate("common.loading") || "Loading contact page..."}</p>
          </div>
        ) : (
          <>
            {/* Page Heading */}
            <div className="w-full bg-primary border-b-2 border-gray px-6 py-4 mb-8 min-h-[120px]">
              <div className="max-w-[1600px] mx-auto"> 
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {translate("contactPage.heading")}
              </h1>
              <p className="text-gray-400 max-w-2xl text-sm md:text-base">
                {translate("contactPage.subheading")}
              </p>
              </div>
            </div>

            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16 px-4 md:px-6 py-10 md:py-16 ">
              {/* Form */}
              <div className="bg-card rounded-lg p-6 md:p-8 order-1">
                <h2 className="text-xl font-bold text-white mb-6">
                  {translate("contactPage.title")}
                </h2>

                {submitMessage.text && (
                  <div
                    className={`mb-6 p-4 rounded-lg text-sm ${submitMessage.type === "success"
                        ? "bg-green-900/40 border border-green-700/50 text-green-300"
                        : "bg-red-900/40 border border-red-700/50 text-red-300"
                      }`}
                  >
                    {submitMessage.text}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5 ">
                  {/* ... form fields remain exactly the same ... */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">
                        {translate("contactPage.labels.name")}
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={translate("contactPage.placeholders.name")}
                        className="w-full bg-page border border-default rounded-lg px-4 py-3 text-white outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">
                        {translate("contactPage.labels.email")}
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={translate("contactPage.placeholders.email")}
                        className="w-full bg-page border border-default rounded-lg px-4 py-3 text-white outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">
                      {translate("contactPage.labels.phone")}
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                        <Phone size={18} />
                      </span>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+971 50 000 0000"
                        className={`${isRTL ? "pr-12" : "pl-12"} w-full bg-page border border-default rounded-lg px-4 py-3 text-white outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition`}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">
                      {translate("contactPage.labels.subject")}
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full bg-page border border-default rounded-lg px-4 py-3 text-white outline-none appearance-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition"
                    >
                      <option value="">{translate("contactPage.subjects.general")}</option>
                      <option value="sales">{translate("contactPage.subjects.sales")}</option>
                      <option value="support">{translate("contactPage.subjects.support")}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">
                      {translate("contactPage.labels.message")}
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={translate("contactPage.placeholders.message")}
                      rows={5}
                      className="w-full bg-page border border-default rounded-lg px-4 py-3 text-white outline-none resize-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-primary hover:bg-primary-dark text-white font-bold py-3.5 rounded-lg transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="animate-spin" size={20} />
                        {translate("contactPage.submitting") || "Sending..."}
                      </>
                    ) : (
                      translate("contactPage.submit")
                    )}
                  </button>
                </form>
              </div>

              {/* Right Side: Cards + Image + Socials */}
              <div className="space-y-6 order-2">
                {/* Contact Info Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {contactCards.length > 0 ? (
                    contactCards.map((card, idx) => {
                      const IconComponent = getCardIcon(card);

                      return (
                        <div
                          key={idx}
                          className="bg-card rounded-xl p-6 border border-default transition-all duration-300"
                        >
                          <div className="text-blue-500 mb-4">
                            <IconComponent className="w-8 h-8" />
                          </div>
                          <h3 className="font-semibold text-white text-base mb-2">
                            {card.card_title?.value || "Contact Info"}
                          </h3>
                          <div
                            className="text-gray-300 text-sm leading-relaxed"
                            dangerouslySetInnerHTML={{
                              __html: card.main_content?.value || "<p>No details provided</p>",
                            }}
                          />
                          {card.sub_content?.value && (
                            <p className="text-gray-500 text-xs mt-3 italic">
                              {card.sub_content.value}
                            </p>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    // Fallback cards remain the same
                    <>
                      <div className="bg-card rounded-xl p-6 border border-default ">
                        <div className="text-primary mb-4"><Building2 className="w-8 h-8" /></div>
                        <h3 className="font-semibold text-white text-base mb-2">
                          {contactDetails.office_title?.value || "Office"}
                        </h3>
                        <p className="text-gray-300 text-sm">
                          {contactDetails.address?.value || "No address available"}
                        </p>
                      </div>

                      <div className="bg-card rounded-xl p-6 border border-default ">
                        <div className="text-primary mb-4"><Phone className="w-8 h-8" /></div>
                        <h3 className="font-semibold text-white text-base mb-2">
                          {contactDetails.phone_title?.value || "Phone"}
                        </h3>
                        <p className="text-gray-300 text-sm">
                          {contactDetails.phone?.value || "No phone available"}
                        </p>
                      </div>

                      <div className="bg-card rounded-xl p-6 border border-default ">
                        <div className="text-primary mb-4"><Mail className="w-8 h-8" /></div>
                        <h3 className="font-semibold text-white text-base mb-2">
                          {contactDetails.email_title?.value || "Email"}
                        </h3>
                        <p className="text-gray-300 text-sm">
                          {contactDetails.email?.value || "No email available"}
                        </p>
                      </div>
                    </>
                  )}
                </div>

                {/* Right-side image (large screens) */}
                {rightImage && (
                  <div className="hidden lg:block mt-8 lg:mt-0">
                    <img
                      src={rightImage}
                      alt="Contact illustration"
                      className="w-full h-auto rounded-xl shadow-2xl object-cover"
                    />
                  </div>
                )}

                {/* Social Links – only real URLs */}
                {validSocialLinks.length > 0 && (
                  <div className="pt-4">
                    <div className="flex items-center gap-4 flex-wrap">
                      <span className="text-sm font-semibold uppercase tracking-wider text-gray-300">
                        {translate("contactPage.followus") || "Follow Us"}
                      </span>

                      <div className={`flex gap-4 ${isRTL ? "flex-row-reverse" : ""}`}>
                        {validSocialLinks.map((social, idx) => {
                          const { url, platform, customIcon } = social;
                          const Icon = socialIconMap[platform] || HelpCircle;

                          return (
                            <a
                              key={idx}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="group"
                              aria-label={platform}
                            >
                              <div className={`
                                w-11 h-11 rounded-full flex items-center justify-center
                                bg-page/70 border border-slate-600
                                transition-all duration-300
                                group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-blue-500/20
                                ${customIcon ? "" : "text-gray-300 group-hover:text-blue-400"}
                              `}>
                                {customIcon ? (
                                  <img
                                    src={customIcon}
                                    alt={platform}
                                    className="w-6 h-6 object-contain"
                                  />
                                ) : (
                                  <Icon size={22} strokeWidth={1.8} />
                                )}
                              </div>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* FAQ Teaser Section – unchanged */}
            <div className="text-center border-t border-slate-800 pt-12 px-4 pb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                {faqTeaser.title?.value || "Frequently Asked Questions"}
              </h2>

              <div
                className="text-gray-300 max-w-3xl mx-auto text-base leading-relaxed mb-8"
                dangerouslySetInnerHTML={{
                  __html: faqTeaser.description?.value || "<p>Quick solutions for common issues...</p>",
                }}
              />

              {faqTeaser.button_text?.value && (
                <Link
                  to={faqTeaser.button_link?.value || "/help-center"}
                  className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-slate-800 border border-blue-600/40 text-blue-400 hover:bg-blue-600/20 hover:text-blue-300 transition-all duration-300 font-medium"
                >
                  {faqTeaser.button_text.value}
                </Link>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ContactPage;